import {
  AggregateQuestionVisualizationEnum,
  ChartOrderBy,
  DisplayAggregateQuestionDto,
  DisplayIndividualQuestionDto,
  IndividualQuestionVisualizationEnum,
  QuestionChoice,
  QuestionComponentDto,
} from '@keyops-cep/api-client';

import { MaxDiffRow } from '../../components/Visualization/MaxDiff';
import { ISurveySparrowAnswerData } from '../../pages/EngagementDetails/types';

import { Count, Counts } from './stats';

/**
 * This function is used to wrap a graph y axis label in the number of characters and lines provided
 */
export function splitStringIntoChunks(
  str: string,
  maxChars: number,
  maxLines: number
) {
  const chunks: string[] = [];
  let currentChunk = '';
  let truncated = false;

  str.split(' ').forEach((word: string) => {
    if (chunks.length < maxLines) {
      if ((currentChunk + word).length <= maxChars) {
        currentChunk += word + ' ';
      } else {
        if (currentChunk) {
          chunks.push(currentChunk.trim());
        }
        currentChunk = word + ' ';
      }
    } else {
      truncated = true;
    }
  });

  if (truncated) {
    chunks[maxLines - 1] += '...';
  } else if (currentChunk && chunks.length < maxLines) {
    chunks.push(currentChunk.trim());
  }

  return chunks;
}

// get latest previous average from annotations, if it is available
export const getPrevAvgAnnotation = (
  question: DisplayIndividualQuestionDto | DisplayAggregateQuestionDto
) =>
  question.questionAnnotations?.find(
    (annotation) => annotation.name === 'PreviousAverage'
  );

// Extract unique header labels from choice labels
export const getHeaderLabelsFromChoices = (choices?: QuestionChoice[]) => {
  return Array.from(
    new Set(
      (choices ?? [])
        .map((choice) => choice.label?.split(':')[0]?.trim())
        .filter(Boolean)
    )
  );
};

// Determine if all coloned choices have numeric values after the colon
export const isNumericChoices = (choices?: QuestionChoice[]): boolean => {
  if (!choices || choices.length === 0) return false;

  const colonedChoices = choices.filter((choice) => choice.label.includes(':')); // exclude choice without an colon

  if (colonedChoices.length === 0) return false;

  return colonedChoices.every((choice) => {
    const labelPrefix = choice.label.split(':')[1].trim();
    const num = parseInt(labelPrefix, 10);
    return !isNaN(num);
  });
};

// Compute average values and label type from multi-select numeric answers.
// Example output: [{ componentId: 'c1', Knowledge: 4.5, Importance: 7 }, { componentId: 'c2', Knowledge: 3.2, Importance: 5 }]
interface CellAverages {
  componentId: string;
  [label: string]: number | 'N/A' | string;
}
export const computeMatrixComponentAverages = (
  questionAnswers: (ISurveySparrowAnswerData | undefined)[],
  headerLabels: string[]
): CellAverages[] => {
  const answerComponentsMatrix = questionAnswers.map(
    (ans) => ans?.components ?? []
  );

  const allComponentIds = new Set<string>();
  answerComponentsMatrix.forEach((components) => {
    components.forEach((comp) => {
      if (comp.questionComponentSurveySparrowId) {
        allComponentIds.add(comp.questionComponentSurveySparrowId);
      }
    });
  });

  return Array.from(allComponentIds).map((componentId) => {
    const responses = answerComponentsMatrix
      .map((components) =>
        components.find(
          (c) => c.questionComponentSurveySparrowId === componentId
        )
      )
      .filter(Boolean);

    const sums: Record<string, number> = {};
    const counts: Record<string, number> = {};

    responses.forEach((entry) => {
      const values = Array.isArray(entry?.value) ? entry.value : [entry?.value];

      values.forEach((val) => {
        if (val === null || val === undefined) return;
        const strVal = String(val);
        const [label] = strVal.split(':');
        const type = label.trim();
        const numMatch = strVal.match(/\d+/);
        if (!numMatch) return;

        const num = parseInt(numMatch[0], 10);
        if (!isNaN(num)) {
          sums[type] = (sums[type] || 0) + num;
          counts[type] = (counts[type] || 0) + 1;
        }
      });
    });

    const result: CellAverages = { componentId };
    headerLabels.forEach((type) => {
      result[type] = counts[type]
        ? parseFloat((sums[type] / counts[type]).toFixed(2))
        : 'N/A';
    });

    return result;
  });
};

// format an axis labels in normal and expanded views
export const formatAxisLabels = (
  label: string,
  labelLimit: number,
  isExpanded: boolean | undefined
) => {
  return isExpanded
    ? label
    : label.length > labelLimit
    ? `${label.substring(0, labelLimit)}...`
    : label;
};

// Extracts label frequencies for each component and column label
// [
//   { componentId: 'c1', Knowledge: { '4': 2, '5': 1 }, Importance: { '1 - very low': 1, '3': 1, '6': 1} },
//   ...
// ]
interface NonNumericFrequencyRow {
  componentId: string;
  [columnLabel: string]: Record<string, number> | string; // e.g., { "Better": 4, "Worse": 2 }
}
export function extractLabelFrequenciesForMatrix(
  answers: (ISurveySparrowAnswerData | undefined)[],
  columnLabels: string[]
): NonNumericFrequencyRow[] {
  const frequencies: Record<string, NonNumericFrequencyRow> = {};

  for (const answer of answers) {
    if (!answer?.components) continue;

    for (const component of answer.components) {
      const componentId = component.questionComponentSurveySparrowId;
      const values = Array.isArray(component.value)
        ? component.value
        : [component.value];

      if (!frequencies[componentId]) {
        frequencies[componentId] = { componentId };
      }

      for (const val of values) {
        // Ex: "CKD: Better"
        const [colLabelRaw, answerLabelRaw] = (val as string)
          .split(':')
          .map((s) => s?.trim());

        if (!colLabelRaw || !answerLabelRaw) continue;

        // Find matching official column label (in case of minor mismatches or extra spaces)
        const colLabel = columnLabels.find((c) =>
          colLabelRaw.toLowerCase().startsWith(c.toLowerCase())
        );
        if (!colLabel) continue;

        const answerLabel = answerLabelRaw;

        const existing = frequencies[componentId][colLabel] as
          | Record<string, number>
          | undefined;

        if (!existing) {
          frequencies[componentId][colLabel] = { [answerLabel]: 1 };
        } else {
          existing[answerLabel] = (existing[answerLabel] ?? 0) + 1;
        }
      }
    }
  }

  // Return as array
  return Object.values(frequencies);
}

/*
  Main function to sort graph data based on the visualization type and orderBy option.
*/
export const sortGraphResults = (
  visualizationType:
    | AggregateQuestionVisualizationEnum
    | IndividualQuestionVisualizationEnum,
  orderBy: ChartOrderBy | undefined,
  dataToSort: Counts | Count[] | MaxDiffRow[] | QuestionComponentDto[]
): Counts | Count[] | MaxDiffRow[] | QuestionComponentDto[] => {
  switch (visualizationType) {
    case IndividualQuestionVisualizationEnum.Histogram:
    case IndividualQuestionVisualizationEnum.HorizontalBarGraphOfAverages:
      return sortGraphDataWithCounts(orderBy, dataToSort as Counts);

    case IndividualQuestionVisualizationEnum.Distribution:
      return sortGraphDataWithCounts(orderBy, dataToSort as Counts, true);

    case IndividualQuestionVisualizationEnum.HorizontalHistogram:
      return sortHorizontalHistogramData(orderBy, dataToSort as Count[]);

    case AggregateQuestionVisualizationEnum.MaxDiff:
      return sortMaxDiffData(orderBy, dataToSort as MaxDiffRow[]);

    case AggregateQuestionVisualizationEnum.StackedBarChart:
      return sortStackedBarChartLabels(
        orderBy,
        dataToSort as QuestionComponentDto[]
      );

    default:
      return dataToSort;
  }
};

/*
  Sort Horizontal bar, Histogram (no range) and Distribution charts results based on the orderBy option.
  For Distribution graphs, alphabetical sorting is not applied to keep the initial order.
  */
export const sortGraphDataWithCounts = (
  orderBy: ChartOrderBy | undefined,
  counts: Counts,
  isDistributionGraph = false
): Counts => {
  const entries = Object.entries(counts);

  switch (orderBy) {
    case ChartOrderBy.VALUEASC:
      return Object.fromEntries(
        entries.toSorted(([, valueA], [, valueB]) => valueA - valueB)
      );
    case ChartOrderBy.VALUEDESC:
      return Object.fromEntries(
        entries.toSorted(([, valueA], [, valueB]) => valueB - valueA)
      );
    case ChartOrderBy.ALPHABETICALASC: {
      if (isDistributionGraph) {
        return counts; // No sorting, keep initial order for Distribution graphs
      }
      return Object.fromEntries(
        entries.toSorted(([keyA], [keyB]) => keyA.localeCompare(keyB))
      );
    }
    case ChartOrderBy.ALPHABETICALDESC: {
      if (isDistributionGraph) {
        return counts; // No sorting, keep initial order for Distribution graphs
      }
      return Object.fromEntries(
        entries.toSorted(([keyA], [keyB]) => keyB.localeCompare(keyA))
      );
    }
    case ChartOrderBy.INITIAL:
    case undefined:
    default:
      return counts; // No sorting, keep initial order
  }
};

/*
 * Sort Horizontal Histogram results based on the orderBy option.
 */
export const sortHorizontalHistogramData = (
  orderBy: ChartOrderBy | undefined,
  counts: Count[]
): Count[] => {
  switch (orderBy) {
    case ChartOrderBy.VALUEASC:
      return counts.toSorted((a, b) => a.value - b.value);
    case ChartOrderBy.VALUEDESC:
      return counts.toSorted((a, b) => b.value - a.value);
    case ChartOrderBy.ALPHABETICALASC:
      return counts.toSorted((a, b) => a.label.localeCompare(b.label));
    case ChartOrderBy.ALPHABETICALDESC:
      return counts.toSorted((a, b) => b.label.localeCompare(a.label));
    case ChartOrderBy.INITIAL:
    case undefined:
    default:
      return counts; // No sorting, keep initial order
  }
};

/* 
  Sort MaxDiff results based on the orderBy option.
  Mutates the input rows array.
*/
export const sortMaxDiffData = (
  orderBy: ChartOrderBy | undefined,
  rows: MaxDiffRow[]
): MaxDiffRow[] => {
  switch (orderBy) {
    case ChartOrderBy.VALUEASC:
      return rows.toSorted(
        (a, b) => a.preferenceSharePercent - b.preferenceSharePercent
      );
    case ChartOrderBy.VALUEDESC:
      return rows.toSorted(
        (a, b) => b.preferenceSharePercent - a.preferenceSharePercent
      );
    case ChartOrderBy.ALPHABETICALASC:
      return rows.toSorted((a, b) => a.label.localeCompare(b.label));
    case ChartOrderBy.ALPHABETICALDESC:
      return rows.toSorted((a, b) => b.label.localeCompare(a.label));
    case ChartOrderBy.INITIAL:
    case undefined:
    default:
      return rows; // No sorting, keep initial order
  }
};

/*
  Sort Stacked Bar Chart question's components labels based on the orderBy option.
*/
export const sortStackedBarChartLabels = (
  orderBy: ChartOrderBy | undefined,
  questionComponents: QuestionComponentDto[]
): QuestionComponentDto[] => {
  switch (orderBy) {
    case ChartOrderBy.ALPHABETICALASC:
      return questionComponents.toSorted((a, b) =>
        a.label.localeCompare(b.label)
      );
    case ChartOrderBy.ALPHABETICALDESC:
      return questionComponents.toSorted((a, b) =>
        b.label.localeCompare(a.label)
      );
    // Not applicable for StackedBarChart, as each row has multiple values
    case ChartOrderBy.VALUEASC:
    case ChartOrderBy.VALUEDESC:
    case ChartOrderBy.INITIAL:
    case undefined:
    default:
      return questionComponents; // No sorting, keep initial order
  }
};
